import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const PROGRESS_ANIMATION_MS = 750;

export type CircularProgressSize = 3.25 | 4 | 5 | 6 | 8;

const SIZE_TO_CONTAINER_CLASS: Record<CircularProgressSize, string> = {
    3.25: 'size-[13px]',
    4: 'size-4',
    5: 'size-5',
    6: 'size-6',
    8: 'size-8',
};

const SIZE_TO_RING_CLASS: Record<CircularProgressSize, string> = {
    3.25: 'border',
    4: 'border',
    5: 'border-2',
    6: 'border-2',
    8: 'border-[3px]',
};

const SIZE_TO_FILL_INSET_CLASS: Record<CircularProgressSize, string> = {
    3.25: 'inset-[3px]',
    4: 'inset-0.5',
    5: 'inset-1',
    6: 'inset-1',
    8: 'inset-1.5',
};

interface CircularProgressProps {
    progress: number;
    size?: CircularProgressSize;
    onDisplayedProgressChange?: (displayedProgress: number) => void;
    animateProgress?: boolean;
    className?: string;
}

function clampProgress(value: number): number {
    return Math.min(100, Math.max(0, value));
}

export function CircularProgress({
    progress,
    size = 5,
    onDisplayedProgressChange,
    animateProgress = true,
    className,
}: CircularProgressProps) {
    const targetProgress = clampProgress(progress);
    const [animatedDisplayed, setAnimatedDisplayed] = useState(0);
    const displayedProgressRef = useRef(0);
    const onDisplayedProgressChangeRef = useRef(onDisplayedProgressChange);
    onDisplayedProgressChangeRef.current = onDisplayedProgressChange;

    const displayedProgress = animateProgress
        ? animatedDisplayed
        : targetProgress;

    useEffect(() => {
        if (!animateProgress) {
            onDisplayedProgressChangeRef.current?.(targetProgress);
            return;
        }

        const from = displayedProgressRef.current;
        if (from === targetProgress) {
            return;
        }

        const startTime = performance.now();
        let frameId = 0;

        const tick = (now: number) => {
            const elapsed = now - startTime;
            const t = Math.min(1, elapsed / PROGRESS_ANIMATION_MS);
            if (t >= 1) {
                displayedProgressRef.current = targetProgress;
                setAnimatedDisplayed(targetProgress);
                onDisplayedProgressChangeRef.current?.(targetProgress);
                return;
            }
            const eased = 1 - (1 - t) ** 3;
            const value = from + (targetProgress - from) * eased;
            displayedProgressRef.current = value;
            setAnimatedDisplayed(value);
            onDisplayedProgressChangeRef.current?.(value);
            frameId = requestAnimationFrame(tick);
        };

        frameId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameId);
    }, [targetProgress, animateProgress]);

    const angle = (displayedProgress / 100) * 360;

    return (
        <div
            className={cn(
                'relative inline-flex shrink-0 text-white-100',
                SIZE_TO_CONTAINER_CLASS[size],
                className,
            )}
        >
            <div
                className={cn(
                    'absolute inset-0 rounded-full border-current',
                    SIZE_TO_RING_CLASS[size],
                )}
            />
            <div
                className={cn(
                    'circular-progress-fill absolute overflow-hidden rounded-full',
                    SIZE_TO_FILL_INSET_CLASS[size],
                )}
                style={{ '--progress-angle': `${angle}deg` } as CSSProperties}
            />
        </div>
    );
}
